import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Music, Compass, Sparkles, TrendingUp, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { proxyImage } from "@/lib/img";
import { runToolJob, TOOL_ETA } from "@/lib/toolJobs";
import JobProgress from "@/components/app/JobProgress";
import BrandDnaCard from "@/components/app/BrandDnaCard";
import { useBrandDnaSummary } from "@/lib/useBrandDna";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "tt-sounds", label: "TikTok Trending Sounds", icon: Music },
  { id: "ig-trending", label: "Virale Posts in deiner Nische", icon: Compass },
];

export default function TrendsHubPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "tt-sounds");
  const [country, setCountry] = useState("DE");
  const [niche, setNiche] = useState("");
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(null);
  const [data, setData] = useState(null);
  const { summary: dna } = useBrandDnaSummary();

  useEffect(() => { setSearchParams({ tab }); /* eslint-disable-next-line */ }, [tab]);

  // Pre-fill niche from Brand DNA summary if available
  useEffect(() => {
    if (dna?.niche && !niche) setNiche(dna.niche);
    // eslint-disable-next-line
  }, [dna]);

  // Re-open a past run via ?jobId=...
  useEffect(() => {
    const jobId = searchParams.get("jobId");
    if (!jobId) return;
    (async () => {
      try {
        const { data: job } = await axios.get(`${API}/tools/jobs/${jobId}`);
        if (job.status === "completed" && job.result) {
          setData(job.result);
          if (job.input?.niche) setNiche(job.input.niche);
          setTab("ig-trending");
        } else if (job.status === "failed") {
          toast.error(job.error || "Diese Analyse ist fehlgeschlagen");
        } else if (job.status === "running") {
          toast.info("Analyse läuft noch — bitte gleich nochmal probieren");
        }
      } catch (_) { /* silent */ }
    })();
  }, [searchParams]);

  const loadTikTokSounds = useCallback(async () => {
    setLoading(true); setData(null); setTick(null);
    try {
      const res = await axios.get(`${API}/tools/tiktok-trends?country=${country}`);
      setData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "TikTok-Trends konnten nicht geladen werden"); }
    finally { setLoading(false); }
  }, [country]);

  const loadNicheTrending = async () => {
    if (!niche.trim()) { toast.error("Bitte trage deine Nische ein"); return; }
    setLoading(true); setData(null); setTick(null);
    try {
      const res = await runToolJob("post", "/tools/instagram-trending", { niche: niche.trim() }, {
        etaSec: TOOL_ETA["instagram-trending"], onTick: setTick,
      });
      setData(res);
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Virale Posts konnten nicht geladen werden"); }
    finally { setLoading(false); setTick(null); }
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="h-8 w-8 text-zinc-400 hover:text-white"><ArrowLeft className="w-4 h-4" /></Button>
          <TrendingUp className="w-5 h-5 text-cyan-400" />
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Trends-Hub</h1>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-zinc-800 pb-3">
          {TABS.map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); setData(null); }} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium transition-all ${tab === t.id ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/40" : "text-zinc-400 hover:text-zinc-200 border border-transparent"}`} data-testid={`tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>

        {/* TT Sounds */}
        {tab === "tt-sounds" && (
          <div className="space-y-4">
            <div className="flex items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[120px]">
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Land</label>
                <Input value={country} onChange={e => setCountry(e.target.value.toUpperCase().slice(0, 2))} placeholder="DE" className="bg-zinc-900 border-zinc-800 text-white h-10" data-testid="trends-country" />
              </div>
              <Button onClick={loadTikTokSounds} disabled={loading} className="bg-cyan-500 hover:bg-cyan-400 text-white h-10" data-testid="load-tt-sounds">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" />Hot Sounds laden</>}
              </Button>
            </div>
            <p className="text-xs text-zinc-500">Aktuell trendige TikTok-Sounds für die letzten 24-72h. Nutze sie für deine nächsten Reels — der Algorithmus pusht alles was darauf läuft.</p>
            {data?.sounds?.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="tt-sounds-grid">
                {data.sounds.map((s, i) => (
                  <div key={i} className="p-3 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-cyan-500/40 transition-all flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-zinc-800 flex-shrink-0 overflow-hidden">
                      {s.cover ? <img src={proxyImage(s.cover)} alt="" className="w-full h-full object-cover" /> : <Music className="w-5 h-5 text-zinc-600 m-auto mt-3.5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-white truncate">{s.title}</p>
                      <p className="text-xs text-zinc-500 truncate">{s.author}</p>
                      <p className="text-[10px] text-zinc-600">{s.video_count?.toLocaleString()} Videos · {s.duration}s</p>
                    </div>
                    {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="text-cyan-400 hover:text-cyan-300"><ExternalLink className="w-4 h-4" /></a>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Niche-based viral posts */}
        {tab === "ig-trending" && (
          <div className="space-y-4">
            {dna && <BrandDnaCard summary={dna} variant="hint" />}
            <div className="flex gap-2 flex-wrap">
              <Input
                value={niche}
                onChange={e => setNiche(e.target.value)}
                onKeyDown={e => e.key === "Enter" && loadNicheTrending()}
                placeholder="Deine Nische — z.B. Mindset Coaching für Frauen ab 40, oder #mindset, #coaching"
                className="bg-zinc-900 border-zinc-800 text-white h-10 flex-1 min-w-[280px]"
                data-testid="niche-input"
              />
              <Button onClick={loadNicheTrending} disabled={loading || !niche.trim()} className="bg-cyan-500 hover:bg-cyan-400 text-white h-10" data-testid="load-niche-viral">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" />Virale Posts finden</>}
              </Button>
            </div>
            <p className="text-xs text-zinc-500">
              Trag deine Nische als Freitext oder Hashtags ein — wir holen die <strong className="text-zinc-300">viralsten</strong> Instagram-Posts der letzten Tage dazu, sortiert nach Engagement.
              Tipp: <em>"Brand DNA"</em> hilft dir, deine Nische präzise zu formulieren — dann fließt sie hier automatisch ein.
            </p>
            {loading && tab === "ig-trending" && <JobProgress tick={tick} label="Virale Posts in deiner Nische werden gesammelt…" />}
            {data?.hashtags_used?.length > 0 && !loading && (
              <p className="text-[11px] text-zinc-500" data-testid="hashtags-used">
                Genutzte Hashtags: {data.hashtags_used.map(h => `#${h}`).join(" · ")}
              </p>
            )}
            {data?.posts?.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="niche-grid">
                {data.posts.map((p, i) => (
                  <a key={i} href={p.url} target="_blank" rel="noreferrer" className="group block">
                    {p.thumbnail && <img src={proxyImage(p.thumbnail)} alt="" className="w-full aspect-square rounded-lg object-cover border border-zinc-800 group-hover:border-cyan-500/40 transition-all" />}
                    <p className="text-[10px] text-zinc-400 mt-1 truncate">@{p.owner_username}</p>
                    <p className="text-[10px] text-zinc-500">❤️ {p.likes?.toLocaleString()} · 👁 {p.views?.toLocaleString()}</p>
                    {p.caption && <p className="text-[10px] text-zinc-600 mt-0.5 line-clamp-2">{p.caption}</p>}
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
